// LE VESTIAIRE (26/07, refondu le soir même après essai de Claude-Alain : « la liste du dessous est
// trop longue à être consultée et on s'y perd. Je veux un vestiaire plus moderne et avec plus de
// fonctions »).
//
// Ce qui a changé, et pourquoi :
//   · ONGLETS au lieu d'une seule page qui déroule. On ne consulte plus, on choisit : chaque
//     famille de cosmétiques tient dans un écran, sans défilement interminable.
//   · TOUT est en vignettes qui MONTRENT la chose (le héros portant la tenue, le familier, la
//     palette de l'effet) — un nom seul ne dit rien de ce qu'on va obtenir.
//   · Ce qu'on ne possède PAS est montré, verrouillé et grisé, avec un compteur « 3/8 » par onglet.
//     Un vestiaire qui ne montre que l'acquis ne donne envie de rien.
//   · L'aperçu se REGARDE : trois poses au choix (repos, course, dash), et un bouton « Surprends-
//     moi » qui tire une panoplie au hasard dans ce qu'on possède.
//
// Chaque changement se voit immédiatement (mise à jour optimiste), avant la réponse du serveur.
// Rien ici n'entre dans la simulation.
import { apiEquip, apiListCampaigns, apiMineState, apiSetSkin, apiUse } from './api'
import { apercuEntree, stopApercus } from './entree-apercu'
import { blitPet } from './pet-papier'
import { ENTREE_CAMPAGNE, ENTREES, SLOT_ENTREE } from '../shared/entrees'
import type { CampaignSummary } from '../shared/api-types'
import { drawBaroudeur, type PoseName } from './baroudeur'
import { boostActive, showToast } from './economy-ui'
import { setEquippedLocal, trailDashColors, trailDigColors } from './loadout'
import { AVATAR_HUES, hueIndex, SKIN_IDS } from '../shared/skins'
import { LOOTS, type Loot, type LootSlot } from '../shared/loots'
import { ACCESSOIRES } from '../shared/accessoires'
import { OUTFITS } from '../shared/outfits'
import { avatarHue, setAvatarHue } from './prefs'

const OR = '#ffd23f'
const ENCRE = '#f0e9d2'
const GRIS = '#9a93c0'
const RARITY_COLOR: Record<string, string> = { courant: '#c9c4de', rare: '#5db4ff', epique: '#b98bff', mega: OR }

/** Les familiers qui VOLENT : dans l'aperçu, ils flottent au lieu de se poser. */
const PET_VOLANTS = new Set(['pet-canari', 'pet-perroquet', 'pet-chouette', 'pet-dragonnet', 'pet-follet'])

/** Les trois poses proposées sous l'aperçu. Le dash montre l'allure, la course montre la tenue. */
const POSES_APERCU: readonly { pose: PoseName; label: string }[] = [
  { pose: 'idle', label: 'Repos' },
  { pose: 'run', label: 'Course' },
  { pose: 'dash', label: 'Dash' },
]

type OngletId = 'tenue' | 'chapeau' | 'couleur' | 'familier' | 'effets' | 'entree' | 'titre'
const ONGLETS: readonly { id: OngletId; ico: string; nom: string }[] = [
  { id: 'tenue', ico: '👕', nom: 'Tenue' },
  { id: 'chapeau', ico: '🎩', nom: 'Chapeau' },
  { id: 'couleur', ico: '🎨', nom: 'Couleur' },
  { id: 'familier', ico: '🐾', nom: 'Familier' },
  { id: 'effets', ico: '✨', nom: 'Effets' },
  { id: 'entree', ico: '🎬', nom: 'Entrée' },
  { id: 'titre', ico: '🏅', nom: 'Titre' },
]

/** Les slots regroupés sous l'onglet « Effets », avec leur libellé de section. */
const SLOTS_EFFETS: readonly { slot: LootSlot; nom: string }[] = [
  { slot: 'trailDash', nom: 'Traînée de dash' },
  { slot: 'trailDig', nom: 'Gravats de creusage' },
  { slot: 'steps', nom: 'Bruit de pas' },
  { slot: 'pickaxe', nom: 'Pioche de casse' },
  { slot: 'victory', nom: 'Effet de victoire' },
  { slot: 'deco', nom: 'Décoration de carte' },
]

/**
 * Ouvre le vestiaire (overlay plein écran, autonome). `onClose` = fermé ; `onChange` = un
 * équipement ou une couleur vient de changer. Renvoie `close()` (idempotent).
 */
export function startVestiaire(opts: { onClose: () => void; onChange?: () => void }): { close(): void } {
  let closed = false
  let inventory = new Set<string>()
  let equippedMap: Record<string, string> = {}
  let boostUntil: string | null = null
  let selHue = hueIndex(avatarHue())
  let onglet: OngletId = 'tenue'
  let poseApercu: PoseName = 'run'
  let charge = false // l'inventaire est arrivé (avant : afficher « 0/8 » serait un mensonge)
  // Les campagnes, pour l'onglet « Entrée » : ce sont elles qui portent la récompense et son
  // mode d'emploi (« Termine la campagne X en solo »). Best-effort : hors ligne, les entrées
  // s'affichent quand même, sans la phrase qui dit où les trouver.
  let campagnes: CampaignSummary[] = []

  // ⚠️ La mise en page vit dans public/index.html (classes ve-*), PAS ici : une media query ne peut
  // pas s'écrire dans un style inline, et un style inline gagnerait contre elle. Mesuré sur
  // téléphone en paysage le 26/07 : l'aperçu prenait 223 px des 390 de l'écran et le pied — donc le
  // bouton « Fermer » — sortait du panneau, coupé par son overflow:hidden.
  const root = document.createElement('div')
  root.className = 've-root'
  const panel = document.createElement('div')
  panel.className = 've-panel'
  root.appendChild(panel)

  const tenue = (): string => equippedMap['outfit'] ?? ''
  const chapeau = (): string => equippedMap['hat'] ?? ''

  // --- l'aperçu vivant --------------------------------------------------------------------------
  const scene = document.createElement('canvas')
  scene.width = 460; scene.height = 190
  scene.className = 've-scene'
  let horloge: number | null = null

  function peindreScene(): void {
    const c = scene.getContext('2d')
    if (!c) return
    c.clearRect(0, 0, scene.width, scene.height)
    c.fillStyle = '#241b45'; c.fillRect(0, 152, scene.width, 38)
    c.fillStyle = 'rgba(255,210,63,0.16)'; c.fillRect(0, 152, scene.width, 2)
    const t = performance.now()
    const halo = c.createRadialGradient(230, 110, 8, 230, 110, 130)
    halo.addColorStop(0, 'rgba(255,210,63,0.11)'); halo.addColorStop(1, 'rgba(255,210,63,0)')
    c.fillStyle = halo; c.fillRect(90, 0, 280, 190)
    const petId = equippedMap['pet']
    if (petId) {
      // Même silhouette qu'en jeu (06/08) : l'aperçu montrait le dessin de l'ancienne feuille, et
      // rien du tout pour les trois derniers compagnons, qui n'y ont pas de ligne.
      const flotte = PET_VOLANTS.has(petId) ? Math.sin(t / 420) * 7 : 0
      c.save()
      c.translate(128, 96 + flotte)
      blitPet(c, petId, 60)
      c.restore()
    }
    const phase = poseApercu === 'idle' ? (t / 1400) % 1 : poseApercu === 'dash' ? 0.32 : (t / 520) % 1
    drawBaroudeur(c, poseApercu, phase, 1, 196, 16, 144, {
      hue: selHue, outfit: tenue(), hat: chapeau(), hatTime: t / 16, dashVariant: 0,
    })
  }

  function close(): void {
    if (closed) return
    closed = true
    if (horloge !== null) window.clearInterval(horloge)
    stopApercus() // sinon les six boucles d'aperçu tourneraient encore, hors du document
    root.remove()
    opts.onClose()
  }
  root.addEventListener('click', (e) => { if (e.target === root) close() })

  /** Équipe (ou retire) : aperçu tout de suite, réseau ensuite. */
  function equip(slot: LootSlot, lootId: string | null): void {
    if (lootId === null) delete equippedMap[slot]
    else equippedMap[slot] = lootId
    setEquippedLocal(slot, lootId)
    peindreScene(); render(); opts.onChange?.()
    apiEquip(slot, lootId)
      .then((r) => { if (!closed) equippedMap = r.equipped })
      .catch((e) => { if (!closed) showToast(e instanceof Error && e.message ? e.message : 'Une erreur est survenue.') })
  }

  function choisirTeinte(h: number): void {
    selHue = h
    setAvatarHue(AVATAR_HUES[h]!)
    void apiSetSkin(SKIN_IDS[0]!, AVATAR_HUES[h]!).catch(() => { /* hors ligne : la pref locale suffit */ })
    peindreScene(); render(); opts.onChange?.()
  }

  function activate(lootId: string): void {
    apiUse(lootId)
      .then((r) => { if (closed) return; boostUntil = r.wallet.boostUntil; opts.onChange?.(); render() })
      .catch((e) => { if (!closed) showToast(e instanceof Error && e.message ? e.message : 'Une erreur est survenue.') })
  }

  /** SURPRENDS-MOI : une panoplie tirée au hasard dans ce qu'on POSSÈDE (jamais du verrouillé). */
  function surprise(): void {
    const tenues = ['', ...OUTFITS.filter((o) => inventory.has(o.id)).map((o) => o.id)]
    const chapeaux = ['', ...ACCESSOIRES.filter((a) => inventory.has(a.id)).map((a) => a.id)]
    const pick = (arr: string[]): string => arr[Math.floor(Math.random() * arr.length)] ?? ''
    const nh = Math.floor(Math.random() * AVATAR_HUES.length)
    selHue = nh
    setAvatarHue(AVATAR_HUES[nh]!)
    void apiSetSkin(SKIN_IDS[0]!, AVATAR_HUES[nh]!).catch(() => { /* hors ligne */ })
    equip('outfit', pick(tenues) || null)
    equip('hat', pick(chapeaux) || null)
  }

  // --- briques d'interface -----------------------------------------------------------------------
  function grille(min: number): HTMLElement {
    const g = document.createElement('div')
    g.style.cssText = `display:grid;grid-template-columns:repeat(auto-fill,minmax(${min}px,1fr));gap:7px`
    return g
  }

  function sousTitre(txt: string): HTMLElement {
    const d = document.createElement('div')
    d.textContent = txt
    d.style.cssText = `margin:14px 0 5px;font-size:12px;font-weight:700;color:${GRIS};letter-spacing:.04em;text-transform:uppercase`
    return d
  }

  /** Carte cliquable : un dessin, un nom, et l'état (équipé / possédé / verrouillé). */
  function carte(o: {
    peindre: (c: CanvasRenderingContext2D, w: number, h: number) => void
    nom: string; equipe: boolean; possede: boolean; rarete?: string
    onClick: () => void; hauteur?: number
  }): HTMLElement {
    const b = document.createElement('button')
    b.type = 'button'
    b.disabled = !o.possede
    const bord = o.equipe ? `2px solid ${OR}` : '1px solid #35305e'
    b.style.cssText = `padding:5px 4px 4px;border-radius:11px;border:${bord};background:${o.equipe ? '#302a5e' : '#1d1840'};display:flex;flex-direction:column;align-items:center;gap:3px;font-family:inherit;cursor:${o.possede ? 'pointer' : 'default'};position:relative;opacity:${o.possede ? 1 : 0.5}`
    const cv = document.createElement('canvas')
    cv.width = 76; cv.height = o.hauteur ?? 84
    cv.style.cssText = 'width:100%;height:auto;image-rendering:pixelated'
    const c = cv.getContext('2d')
    if (c) {
      o.peindre(c, cv.width, cv.height)
      if (!o.possede) { // verrouillé : on le VOIT, en sourdine — c'est ce qui donne envie de creuser
        c.globalCompositeOperation = 'saturation'
        c.fillStyle = '#808080'; c.fillRect(0, 0, cv.width, cv.height)
        c.globalCompositeOperation = 'source-over'
        c.fillStyle = 'rgba(10,8,24,0.45)'; c.fillRect(0, 0, cv.width, cv.height)
      }
    }
    const lab = document.createElement('div')
    lab.textContent = o.nom
    lab.style.cssText = `font-size:10px;line-height:1.2;text-align:center;color:${o.equipe ? OR : o.possede ? ENCRE : GRIS};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%`
    b.append(cv, lab)
    if (o.rarete) { // pastille de rareté, coin haut-droit
      const p = document.createElement('span')
      p.style.cssText = `position:absolute;top:5px;right:5px;width:8px;height:8px;border-radius:50%;background:${RARITY_COLOR[o.rarete] ?? GRIS}`
      b.appendChild(p)
    }
    if (!o.possede) {
      const cad = document.createElement('span')
      cad.textContent = '🔒'
      cad.style.cssText = 'position:absolute;top:4px;left:5px;font-size:12px'
      b.appendChild(cad)
    }
    if (o.equipe) {
      const ok = document.createElement('span')
      ok.textContent = '✓'
      ok.style.cssText = `position:absolute;top:2px;right:6px;font-size:14px;font-weight:800;color:${OR}`
      b.appendChild(ok)
    }
    if (o.possede) b.addEventListener('click', o.onClick)
    return b
  }

  /** Vignette « le héros porte ceci ». */
  const peindreHeros = (hue: number, outfit: string, hat: string) =>
    (c: CanvasRenderingContext2D): void => { drawBaroudeur(c, 'idle', 0.2, 1, 5, 8, 66, { hue, outfit, hat }) }

  /** Vignette d'un familier (sprite HD de pets.png). */
  /**
   * Vignette d'un familier — par sa SILHOUETTE, celle-là même qu'on voit en jouant (06/08).
   *
   * Elle venait de la feuille `pets.png`, qui n'a que DIX lignes : la taupe, la chauve-souris et
   * le crapaud n'avaient donc aucune vignette, et les dix autres en montraient une qui ne
   * ressemblait plus à la bête du jeu depuis le passage au papier découpé. Deux défauts pour une
   * seule cause : la boutique et le terrain ne peignaient pas depuis la même source.
   */
  const peindrePet = (petId: string) => (c: CanvasRenderingContext2D, w: number, h: number): void => {
    const T = Math.min(w, h) * 0.68
    c.save()
    c.translate((w - T) / 2, (h - T) / 2)
    blitPet(c, petId, T)
    c.restore()
  }

  /** Vignette « rien d'équipé » : un tiret, sobre. */
  const peindreAucun = (taille: number) => (c: CanvasRenderingContext2D, w: number, h: number): void => {
    c.font = `${taille}px system-ui, sans-serif`
    c.textAlign = 'center'; c.textBaseline = 'middle'
    c.fillStyle = GRIS
    c.fillText('—', w / 2, h / 2)
  }

  /** Vignette d'un effet : sa palette en gouttes, ou son emoji quand l'effet n'a pas de couleur. */
  const peindreEffet = (l: Loot) => (c: CanvasRenderingContext2D, w: number, h: number): void => {
    const cols = l.slot === 'trailDash' ? trailDashColors(l.id) : l.slot === 'trailDig' ? trailDigColors(l.id) : []
    if (cols.length > 0) {
      cols.forEach((col, i) => {
        const a = (i / cols.length) * Math.PI * 2 - Math.PI / 2
        const r = cols.length === 1 ? 0 : 13
        c.fillStyle = col
        c.beginPath(); c.arc(w / 2 + Math.cos(a) * r, h / 2 + Math.sin(a) * r, 8, 0, Math.PI * 2); c.fill()
      })
      return
    }
    const ico = l.slot === 'steps' ? '👣' : l.slot === 'pickaxe' ? '⛏️' : l.slot === 'victory' ? '🎆' : '🗿'
    c.font = '30px system-ui, "Apple Color Emoji", sans-serif'
    c.textAlign = 'center'; c.textBaseline = 'middle'
    c.fillText(ico, w / 2, h / 2)
  }

  // --- le contenu de chaque onglet ----------------------------------------------------------------
  function contenuTenue(): HTMLElement[] {
    const g = grille(84)
    g.appendChild(carte({
      peindre: peindreHeros(selHue, '', chapeau()), nom: 'Baroudeur', equipe: tenue() === '',
      possede: true, onClick: () => equip('outfit', null),
    }))
    for (const o of OUTFITS) {
      const loot = LOOTS.find((l) => l.id === o.id)
      g.appendChild(carte({
        peindre: peindreHeros(selHue, o.id, chapeau()),
        nom: o.name.replace(/^(Tenue|Combinaison|Scaphandre) (de |d'|des )?/i, ''),
        equipe: tenue() === o.id, possede: inventory.has(o.id), rarete: loot?.rarity,
        onClick: () => equip('outfit', o.id),
      }))
    }
    return [g]
  }

  function contenuChapeau(): HTMLElement[] {
    const g = grille(84)
    g.appendChild(carte({
      peindre: peindreHeros(selHue, tenue(), ''), nom: 'Tête nue', equipe: chapeau() === '',
      possede: true, onClick: () => equip('hat', null),
    }))
    for (const a of ACCESSOIRES) {
      const loot = LOOTS.find((l) => l.id === a.id)
      g.appendChild(carte({
        peindre: peindreHeros(selHue, tenue(), a.id),
        nom: a.name.replace(/^(Chapeau|Casque) (de |à )?/i, ''),
        equipe: chapeau() === a.id, possede: inventory.has(a.id), rarete: loot?.rarity,
        onClick: () => equip('hat', a.id),
      }))
    }
    return [g]
  }

  function contenuCouleur(): HTMLElement[] {
    const g = grille(84)
    for (let h = 0; h < AVATAR_HUES.length; h++) {
      g.appendChild(carte({
        peindre: peindreHeros(h, tenue(), chapeau()), nom: h === 0 ? 'Origine' : `Couleur ${h + 1}`,
        equipe: h === selHue, possede: true, onClick: () => choisirTeinte(h),
      }))
    }
    const note = document.createElement('div')
    note.textContent = 'Les couleurs sont gratuites, et elles marchent avec chaque tenue.'
    note.style.cssText = `font-size:12px;color:${GRIS};margin-top:10px`
    return [g, note]
  }

  function contenuFamilier(): HTMLElement[] {
    const g = grille(84)
    g.appendChild(carte({
      peindre: peindreAucun(30), nom: 'Aucun', equipe: !equippedMap['pet'],
      possede: true, onClick: () => equip('pet', null),
    }))
    for (const l of LOOTS.filter((x) => x.slot === 'pet')) {
      g.appendChild(carte({
        peindre: peindrePet(l.id), nom: l.name, equipe: equippedMap['pet'] === l.id,
        possede: inventory.has(l.id), rarete: l.rarity, onClick: () => equip('pet', l.id),
      }))
    }
    return [g]
  }

  /**
   * L'onglet ENTRÉE. Il ne ressemble à aucun autre, et pour une raison de fond : une entrée en
   * scène n'est faite QUE de mouvement. Une vignette figée de « la réception » montre un bonhomme
   * accroupi, celle de « la lampe » montre un carré noir. Chaque carte JOUE donc son animation en
   * boucle, en grand, à la taille du jeu.
   *
   * Trois états, et chacun dit ce qu'il faut faire :
   *   · possédée .................. cliquable, on l'équipe ;
   *   · mise en jeu par une campagne qu'on n'a pas finie ... « Termine la campagne X en solo » ;
   *   · mise en jeu par personne .. « Prochainement ».
   * L'entrée du mode campagne fait bande à part : elle n'appartient pas au joueur, elle appartient
   * au mode. Elle est montrée, expliquée, et non équipable.
   */
  function contenuEntree(): HTMLElement[] {
    const out: HTMLElement[] = []
    out.push(sousTitre('Ton arrivée en début de niveau'))

    const aucun = document.createElement('button')
    aucun.type = 'button'
    const porteRien = !equippedMap[SLOT_ENTREE]
    aucun.textContent = porteRien ? '✓ Aucune entrée (départ direct)' : 'Aucune entrée (départ direct)'
    aucun.style.cssText = `width:100%;margin:0 0 10px;padding:10px;border-radius:11px;font-family:inherit;font-size:12px;cursor:pointer;border:${porteRien ? `2px solid ${OR}` : '1px solid #35305e'};background:${porteRien ? '#302a5e' : '#1d1840'};color:${porteRien ? OR : ENCRE}`
    aucun.addEventListener('click', () => equip(SLOT_ENTREE, null))
    out.push(aucun)

    for (const e of ENTREES) {
      const possede = inventory.has(e.id)
      const campagne = campagnes.find((c) => c.entree === e.id)
      const parDefaut = e.id === ENTREE_CAMPAGNE
      const equipe = equippedMap[SLOT_ENTREE] === e.id

      const carte = document.createElement('div')
      carte.style.cssText = `margin:0 0 12px;border-radius:14px;overflow:hidden;border:${equipe ? `2px solid ${OR}` : '1px solid #35305e'};background:${equipe ? '#241f4a' : '#181334'}`

      /* L'aperçu à GAUCHE et le texte à DROITE, pas l'un sous l'autre : empilés, six cartes
         faisaient trois écrans de haut et l'on choisissait en faisant défiler. */
      const ligne = document.createElement('div')
      ligne.style.cssText = 'display:flex;align-items:stretch;gap:0'
      const boite = document.createElement('div')
      boite.style.cssText = 'flex:0 0 44%;max-width:230px;background:#171227'
      const cv = document.createElement('canvas')
      cv.style.cssText = `display:block;width:100%;${possede || parDefaut ? '' : 'filter:grayscale(.8) brightness(.62)'}`
      boite.appendChild(cv)
      ligne.appendChild(boite)

      const bas = document.createElement('div')
      bas.style.cssText = 'flex:1;min-width:0;padding:9px 12px 11px;display:flex;align-items:center;gap:10px'
      const txt = document.createElement('div')
      txt.style.cssText = 'flex:1;min-width:0'
      const nom = document.createElement('div')
      nom.textContent = e.nom
      nom.style.cssText = `font-size:13px;font-weight:700;color:${equipe ? OR : possede || parDefaut ? ENCRE : GRIS}`
      const sous = document.createElement('div')
      /* textContent : un nom de campagne est du texte de joueur (même règle que campaigns-ui). */
      sous.textContent = parDefaut ? 'Jouée dans toutes les campagnes, pour tout le monde'
        : possede ? e.desc
          : campagne ? `À gagner : termine la campagne « ${campagne.name} » en solo`
            : 'Prochainement'
      sous.style.cssText = `font-size:11px;line-height:1.45;color:${possede || parDefaut ? GRIS : '#c9a24a'};margin-top:2px`
      txt.append(nom, sous)
      bas.appendChild(txt)

      if (possede && !parDefaut) {
        const b = document.createElement('button')
        b.type = 'button'
        b.textContent = equipe ? '✓ Portée' : 'Porter'
        b.disabled = equipe
        b.style.cssText = `flex:none;padding:7px 14px;border-radius:9px;font-family:inherit;font-size:12px;font-weight:700;border:1px solid ${equipe ? OR : '#4a4380'};background:${equipe ? '#3a3170' : '#2a2456'};color:${equipe ? OR : ENCRE};cursor:${equipe ? 'default' : 'pointer'}`
        b.addEventListener('click', () => equip(SLOT_ENTREE, e.id))
        bas.appendChild(b)
      } else {
        const cadenas = document.createElement('span')
        cadenas.textContent = parDefaut ? '🎬' : '🔒'
        cadenas.style.cssText = 'flex:none;font-size:15px;opacity:.75'
        bas.appendChild(cadenas)
      }
      ligne.appendChild(bas)
      carte.appendChild(ligne)
      out.push(carte)
      /* ⚠️ L'aperçu se monte à la PROCHAINE IMAGE, pas dans une micro-tâche. `render()` construit
         d'abord tous les blocs puis les insère : dans une micro-tâche, le canvas n'est pas encore
         dans le document, `clientWidth` vaut zéro, et la vignette reste NOIRE. C'est exactement ce
         qui s'est passé au premier essai. */
      requestAnimationFrame(() => { if (cv.isConnected) apercuEntree(cv, e.id) })
    }
    return out
  }

  function contenuEffets(): HTMLElement[] {
    const out: HTMLElement[] = []
    for (const { slot, nom } of SLOTS_EFFETS) {
      const items = LOOTS.filter((l) => l.slot === slot)
      if (items.length === 0) continue
      out.push(sousTitre(nom))
      const g = grille(84)
      g.appendChild(carte({
        peindre: peindreAucun(26), nom: 'Aucun', equipe: !equippedMap[slot],
        possede: true, onClick: () => equip(slot, null), hauteur: 62,
      }))
      for (const l of items) {
        g.appendChild(carte({
          peindre: peindreEffet(l),
          nom: l.name.replace(/^(Traînée|Gravats|Pas|Pioche|Effet)\s*(de\s|d'|à\s)?/i, ''),
          equipe: equippedMap[slot] === l.id, possede: inventory.has(l.id), rarete: l.rarity,
          onClick: () => equip(slot, l.id), hauteur: 62,
        }))
      }
      out.push(g)
    }
    // Les consommables (Filon ×2) : possédé = usage débloqué, réarmable une fois expiré.
    const conso = LOOTS.filter((l) => l.type === 'consumable' && inventory.has(l.id))
    if (conso.length > 0) {
      out.push(sousTitre('À activer'))
      for (const l of conso) {
        const actif = l.id === 'boost-x2' && boostActive(boostUntil)
        const row = document.createElement('div')
        row.style.cssText = 'display:flex;align-items:center;gap:8px;margin-top:6px'
        const info = document.createElement('div'); info.style.cssText = 'flex:1;min-width:0'
        const n = document.createElement('div')
        n.textContent = l.name
        n.style.cssText = `font-size:14px;font-weight:700;color:${RARITY_COLOR[l.rarity] ?? ENCRE}`
        const d = document.createElement('div')
        d.textContent = actif ? '✓ Actif (bonus en cours)' : l.desc
        d.style.cssText = `font-size:12px;color:${actif ? '#7fd06a' : GRIS};white-space:nowrap;overflow:hidden;text-overflow:ellipsis`
        info.append(n, d)
        const btn = document.createElement('button')
        btn.type = 'button'; btn.textContent = actif ? '✓ Actif' : 'Activer'; btn.disabled = actif
        btn.style.cssText = `flex:0 0 auto;padding:8px 12px;border-radius:9px;border:0;font-weight:700;font-size:14px;font-family:inherit;${actif ? 'background:#233a2a;color:#7fd06a;cursor:default' : `background:${OR};color:#221b04;cursor:pointer`}`
        if (!actif) btn.addEventListener('click', () => activate(l.id))
        row.append(info, btn)
        out.push(row)
      }
    }
    return out
  }

  function contenuTitre(): HTMLElement[] {
    const liste = document.createElement('div')
    liste.style.cssText = 'display:flex;flex-direction:column;gap:6px'
    const ligne = (texte: string, equipe: boolean, possede: boolean, rarete: string | null, onClick: () => void): HTMLElement => {
      const b = document.createElement('button')
      b.type = 'button'; b.disabled = !possede
      b.style.cssText = `display:flex;align-items:center;gap:8px;padding:9px 11px;border-radius:10px;border:${equipe ? `2px solid ${OR}` : '1px solid #35305e'};background:${equipe ? '#302a5e' : '#1d1840'};color:${possede ? ENCRE : GRIS};font-family:inherit;font-size:13px;text-align:left;cursor:${possede ? 'pointer' : 'default'};opacity:${possede ? 1 : 0.55}`
      const p = document.createElement('span')
      p.style.cssText = `flex:0 0 auto;width:9px;height:9px;border-radius:50%;background:${rarete ? RARITY_COLOR[rarete] ?? GRIS : 'transparent'}`
      const t = document.createElement('span')
      t.textContent = texte
      t.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap'
      b.append(p, t)
      if (!possede) { const c = document.createElement('span'); c.textContent = '🔒'; b.appendChild(c) }
      if (equipe) { const c = document.createElement('span'); c.textContent = '✓'; c.style.color = OR; b.appendChild(c) }
      if (possede) b.addEventListener('click', onClick)
      return b
    }
    liste.appendChild(ligne('Aucun titre', !equippedMap['title'], true, null, () => equip('title', null)))
    for (const l of LOOTS.filter((x) => x.slot === 'title')) {
      liste.appendChild(ligne(
        l.name.replace(/^Titre\s*:\s*/, ''), equippedMap['title'] === l.id, inventory.has(l.id), l.rarity,
        () => equip('title', l.id),
      ))
    }
    const note = document.createElement('div')
    note.textContent = 'Le titre équipé s\'affiche au classement et à la fin des parties.'
    note.style.cssText = `font-size:12px;color:${GRIS};margin-top:10px`
    return [liste, note]
  }

  /** Possédés / total de l'onglet — le compteur qui montre ce qu'il reste à trouver. */
  function compte(id: OngletId): [number, number] {
    if (id === 'couleur') return [AVATAR_HUES.length, AVATAR_HUES.length] // toutes offertes
    const ids = id === 'tenue' ? OUTFITS.map((o) => o.id)
      : id === 'chapeau' ? ACCESSOIRES.map((a) => a.id)
        : id === 'familier' ? LOOTS.filter((l) => l.slot === 'pet').map((l) => l.id)
          : id === 'entree' ? ENTREES.filter((e) => e.id !== ENTREE_CAMPAGNE).map((e) => e.id)
          : id === 'titre' ? LOOTS.filter((l) => l.slot === 'title').map((l) => l.id)
            : LOOTS.filter((l) => SLOTS_EFFETS.some((s) => s.slot === l.slot)).map((l) => l.id)
    return [ids.filter((i) => inventory.has(i)).length, ids.length]
  }

  // --- assemblage ----------------------------------------------------------------------------------
  function render(): void {
    panel.textContent = ''
    // DEUX COLONNES. Empilées sur un écran haut (l'aspect d'origine), côte à côte sur un écran
    // COURT — c'est-à-dire un téléphone tenu en paysage, la position dans laquelle on joue. Là, on
    // dispose de 844 px de large pour 390 de haut : tout empiler affamait la liste (24 px mesurés)
    // et poussait le bouton « Fermer » hors du panneau.
    const colG = document.createElement('div')
    colG.className = 've-col-g'
    const colD = document.createElement('div')
    colD.className = 've-col-d'
    panel.append(colG, colD)

    const tete = document.createElement('div')
    tete.style.cssText = 'padding:14px 16px 10px;display:flex;align-items:baseline;gap:10px'
    const titre = document.createElement('div')
    titre.textContent = 'Le vestiaire'
    titre.style.cssText = `font-size:20px;font-weight:800;color:${OR}`
    const tous = LOOTS.filter((l) => l.type === 'cosmetic')
    const prog = document.createElement('div')
    prog.textContent = charge ? `${tous.filter((l) => inventory.has(l.id)).length}/${tous.length} trouvés` : ''
    prog.style.cssText = `font-size:12px;color:${GRIS};margin-left:auto`
    tete.append(titre, prog)
    colG.appendChild(tete)

    colG.appendChild(scene)

    const barre = document.createElement('div')
    barre.style.cssText = 'display:flex;gap:6px;padding:8px 12px;align-items:center;border-bottom:1px solid #2a2450'
    for (const p of POSES_APERCU) {
      const b = document.createElement('button')
      b.type = 'button'; b.textContent = p.label
      const on = poseApercu === p.pose
      b.style.cssText = `padding:5px 10px;border-radius:8px;border:0;font-size:11px;font-family:inherit;cursor:pointer;background:${on ? '#3a3470' : '#221d45'};color:${on ? OR : GRIS}`
      b.addEventListener('click', () => { poseApercu = p.pose; peindreScene(); render() })
      barre.appendChild(b)
    }
    const surpr = document.createElement('button')
    surpr.type = 'button'; surpr.textContent = '🎲 Surprends-moi'
    surpr.style.cssText = `margin-left:auto;padding:5px 11px;border-radius:8px;border:0;font-size:11px;font-family:inherit;cursor:pointer;background:#2a2450;color:${ENCRE}`
    surpr.addEventListener('click', surprise)
    barre.appendChild(surpr)
    colG.appendChild(barre)

    // Onglets EN PASTILLES : ils s'enroulent sur plusieurs rangs (cf. .ve-tabs), et une pastille
    // pleinement arrondie assume ce retour à la ligne, là où l'ancien onglet à coins hauts se
    // retrouvait détaché de son contenu dès qu'il n'était plus sur la dernière rangée.
    const tabs = document.createElement('div')
    tabs.className = 've-tabs'
    for (const o of ONGLETS) {
      const [ok, total] = compte(o.id)
      const b = document.createElement('button')
      b.type = 'button'
      const on = onglet === o.id
      b.style.cssText = `flex:0 0 auto;padding:7px 10px;border-radius:999px;border:${on ? `1px solid ${OR}` : '1px solid #2f2957'};font-family:inherit;font-size:12px;cursor:pointer;background:${on ? '#302a5e' : '#1d1840'};color:${on ? OR : GRIS};display:flex;align-items:center;gap:5px;white-space:nowrap`
      const ico = document.createElement('span'); ico.textContent = o.ico
      const nom = document.createElement('span'); nom.textContent = o.nom
      b.append(ico, nom)
      if (charge && o.id !== 'couleur') {
        const c = document.createElement('span')
        c.textContent = `${ok}/${total}`
        c.style.cssText = `font-size:10px;color:${ok === total ? '#7fd06a' : '#6f6893'}`
        b.appendChild(c)
      }
      b.addEventListener('click', () => { onglet = o.id; render() })
      tabs.appendChild(b)
    }
    colD.appendChild(tabs)

    // le contenu de l'onglet : la SEULE zone qui défile
    const corps = document.createElement('div')
    corps.style.cssText = 'flex:1;min-height:0;overflow:auto;padding:12px;background:#221d45'
    const blocs = onglet === 'tenue' ? contenuTenue()
      : onglet === 'chapeau' ? contenuChapeau()
        : onglet === 'couleur' ? contenuCouleur()
          : onglet === 'familier' ? contenuFamilier()
            : onglet === 'effets' ? contenuEffets()
              : onglet === 'entree' ? contenuEntree()
              : contenuTitre()
    for (const b of blocs) corps.appendChild(b)
    if (!charge) {
      const att = document.createElement('div')
      att.textContent = 'Chargement de tes trouvailles...'
      att.style.cssText = `font-size:12px;color:${GRIS};margin-top:12px`
      corps.appendChild(att)
    }
    colD.appendChild(corps)

    const pied = document.createElement('div')
    pied.style.cssText = 'padding:10px 12px'
    const fermer = document.createElement('button')
    fermer.type = 'button'; fermer.textContent = 'Fermer'
    fermer.style.cssText = `display:block;width:100%;padding:11px;border-radius:10px;border:0;font-weight:700;font-size:15px;cursor:pointer;font-family:inherit;background:#2a2450;color:${ENCRE}`
    fermer.addEventListener('click', close)
    pied.appendChild(fermer)
    colD.appendChild(pied)
    peindreScene()
  }

  // Amorçage : on montre TOUT DE SUITE le héros (les couleurs marchent hors ligne), l'inventaire suit.
  document.body.appendChild(root)
  render()
  horloge = window.setInterval(() => { if (!closed) peindreScene() }, 60)
  apiMineState()
    .then((s) => {
      if (closed) return
      inventory = new Set(s.inventory); equippedMap = s.equipped; boostUntil = s.wallet.boostUntil
      charge = true
      render()
    })
    .catch(() => { /* hors ligne / non connecté : couleurs et tenue d'origine, sans message d'échec */ })

  // Les campagnes disent quelle entrée est mise en jeu, et si on l'a déjà décrochée. Best-effort
  // strict : un échec laisse l'onglet « Entrée » lisible, seulement sans la phrase « à gagner ».
  apiListCampaigns()
    .then((cs) => { if (!closed) { campagnes = cs; if (onglet === 'entree') render() } })
    .catch(() => { /* hors ligne : les entrées s'affichent sans leur mode d'emploi */ })

  return { close }
}
